import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { DateRangePicker } from "react-date-range";
import { useNavigate } from "react-router-dom";
import {
  FaColumns,
  FaPencilAlt,
  FaShare,
  FaExpand,
  FaCompress,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";
import { MdOutlineFileDownload } from "react-icons/md";
import { BiTask } from "react-icons/bi";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

import ModifyColumns from "./Tools/ModifyColumns";
import BudgetModal from "./BudgetModal";
import LoadingAnimation from "../components/LoadingAnimation";
import RevisedProjectData from "../data/Projectdata";
import {
  getCurrencySymbolFromISO,
  getLocalCurrencyStringType,
} from "./config/tableHelper";
import { RenderTable } from "./config/tableWrapper";
import { generatePDF, generateCSV } from "./Components/DownloadFuncs";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../styles/CustomDateRangePicker.css";

import PaidAdsChart from "./Charts/PaidAdsChart";

const API_URLS = {
  HEADER_DATA: "https://api.confidanto.com/header-data",
  ADS_OVERVIEW_DATA: "https://api.confidanto.com/ads-overview-data",
  GET_CUSTOM_COLUMNS:
    "https://api.confidanto.com/custom-columns-project-performance/get",
  PROJECT_SUMMARY:
    "https://api.confidanto.com/projects-data/subscription/project-summary",
};

const DEMO_EMAILS = [];
const CURRENCY_SYMBOL = "$";

const ALL_SEARCH_ENGINES = [
  {
    id: "google_ads",
    name: "Google Ads",
    platformIds: ["google_ads", "dummy_ads"],
  },
  {
    id: "microsoft_ads",
    name: "Bing Ads",
    platformIds: ["microsoft_ads", "bing_ads"],
  },
];

const INITIAL_COLUMNS = [
  {
    id: "1",
    title: "Project",
    visible: true,
    key: "project_name",
    category: "Recommended",
    isLocked: true,
    type: "string",
  },
  {
    id: "2",
    title: "Tasks",
    visible: true,
    key: "task",
    category: "Recommended",
    isLocked: true,
    type: "string",
  },
  {
    id: "3",
    title: "Impr.",
    visible: true,
    key: "impressions",
    category: "Performance",
    isLocked: false,
    type: "number",
  },
  {
    id: "4",
    title: "Clicks",
    visible: true,
    key: "clicks",
    category: "Performance",
    isLocked: false,
    type: "number",
  },
  {
    id: "8",
    title: "Avg CPC",
    visible: true,
    key: "average_cpc",
    category: "Performance",
    isLocked: false,
    type: "currency",
  },
  {
    id: "5",
    title: "Cost",
    visible: true,
    key: "costs",
    category: "Performance",
    isLocked: false,
    type: "currency",
  },
  {
    id: "6",
    title: "CTR",
    visible: true,
    key: "ctr",
    category: "Performance",
    isLocked: false,
    type: "float",
    calculation: {
      numerator: "clicks",
      denominator: "impressions",
      multiplier: 100,
    },
  },
  {
    id: "7",
    title: "Interactions",
    visible: false,
    key: "interactions",
    category: "Performance",
    isLocked: false,
    type: "number",
  },
  {
    id: "9",
    title: "Interaction Rate",
    visible: false,
    key: "interaction_rate",
    category: "Performance",
    isLocked: false,
    type: "float",
  },
  {
    id: "10",
    title: "Conversions",
    visible: true,
    key: "conversions",
    category: "Conversions",
    isLocked: false,
    type: "number",
  },
  {
    id: "11",
    title: "Cost/Conv",
    visible: false,
    key: "cost_per_conversion",
    category: "Conversions",
    isLocked: false,
    type: "currency",
  },
  {
    id: "12",
    title: "Conv. Rate",
    visible: false,
    key: "conversion_rate",
    category: "Conversions",
    isLocked: false,
    type: "float",
  },
  {
    id: "13",
    title: "Monthly budget",
    key: "monthly_budget",
    category: "Attributes",
    visible: true,
    isLocked: false,
    type: "currency",
  },
  {
    id: "14",
    title: "Planned Daily Spend",
    key: "planned_daily_spend",
    category: "Attributes",
    visible: true,
    isLocked: false,
    type: "currency",
  },
  {
    id: "15",
    title: "Remaining budget for the month",
    key: "remaining_monthly_budget",
    visible: true,
    category: "Attributes",
    isLocked: false,
    type: "currency",
  },
  {
    id: "16",
    title: "Revised Daily spend",
    key: "revised_daily_spend",
    category: "Attributes",
    visible: true,
    isLocked: false,
    type: "currency",
  },
  {
    id: "17",
    title: "Remaining budget",
    visible: false,
    key: "remaining_budget",
    category: "Attributes",
    isLocked: false,
    type: "currency",
  },
  {
    id: "19",
    title: "Daily Reporting Status",
    key: "daily_reporting_status",
    category: "Attributes",
    visible: true,
    isLocked: false,
    type: "string",
  },
  {
    id: "20",
    title: "Weekly Reporting Status",
    key: "weekly_reporting_status",
    category: "Attributes",
    visible: true,
    isLocked: false,
    type: "string",
  },
  {
    id: "21",
    title: "Monthly Reporting Status",
    key: "monthly_reporting_status",
    category: "Attributes",
    visible: false,
    isLocked: false,
    type: "string",
  },
];

function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoogleAdsTable({
  data,
  columns,
  error,
  isLoading,
  onSaveBudget,
  agencyFees,
  setAgencyFees,
  currency,
  totalDaysLeft,
}) {
  const navigate = useNavigate();
  const [localData, setLocalData] = useState([]);
  const [isEditing, setIsEditing] = useState({});
  const [tempBudgets, setTempBudgets] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editableRowIndex, setEditableRowIndex] = useState(null);
  const [userBudget, setUserBudget] = useState({});

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleProjectTitleClick = (item) => {
    localStorage.setItem("project_id", item.project_id);
    localStorage.setItem("customer_id", item.customer_id);
    localStorage.setItem("project_name", item.project_name);
    localStorage.setItem("platform", item.platform);
    localStorage.setItem("ad_account_id", item.ad_account_id);

    let currencySymbol = item.currency
      ? getCurrencySymbolFromISO(item.currency)
      : getCurrencySymbolFromISO("USD");
    localStorage.setItem("currency_symbol", currencySymbol);
    window.dispatchEvent(new CustomEvent("localStorageChange"));
    navigate(`/project-details/${item.project_id}`);
  };

  const handleTaskClick = (item) => {
    if (item.project_id) {
      localStorage.setItem("project_id", item.project_id);
    }
    if (item.project_name) {
      localStorage.setItem("project_name", item.project_name);
    }
    navigate(`/create-task/${item.project_id}`);
  };

  const handleSaveBudget = (newBudget) => {
    setUserBudget((prev) => ({ ...prev, [editableRowIndex]: newBudget }));
    onSaveBudget({ index: editableRowIndex, amount: newBudget });
    setIsModalOpen(false);
  };

  const handleAgencyFeeSave = (id) => {
    const updatedFees = { ...agencyFees, [id]: tempBudgets[id] };
    setAgencyFees(updatedFees);
    setIsEditing((prev) => ({ ...prev, [id]: false }));
  };

  const toggleEditing = (id, value) => {
    setIsEditing((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!isEditing[id]) {
      setTempBudgets((prev) => ({ ...prev, [id]: value }));
    }
  };

  const shareReporting = (key, projectId) => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const newStatus = `Last sent on ${formattedDate}`;

    const updatedData = localData.map((item) => {
      if (item.project_id === projectId) return { ...item, [key]: newStatus };
      return item;
    });
    setLocalData(updatedData);
  };

  const formatCellValue = (key, value, colObj, Currcurrency) => {
    if (value === null || value === undefined || value === "") return 0;

    const number = parseFloat(value);
    if (isNaN(number)) return value;

    let dynamicCurrency = getCurrencySymbolFromISO(Currcurrency)
      ? getCurrencySymbolFromISO(Currcurrency)
      : "$";
    if (colObj.type === "currency") {
      return `${dynamicCurrency}${number.toLocaleString(getLocalCurrencyStringType(dynamicCurrency), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (colObj.type === "percentage" || colObj.type === "float") {
      return `${number.toLocaleString(getLocalCurrencyStringType(dynamicCurrency), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    }
    return number.toLocaleString();
  };

  const getBudgetStatus = (item) => {
    const remaining = parseFloat(item.remaining_monthly_budget) || 0;
    const monthly = parseFloat(item.monthly_budget) || 0;
    const costs = parseFloat(item.costs) || 0;

    let percentage = 0;
    if (monthly > 0) {
      percentage = (remaining / monthly) * 100;
    } else if (remaining + costs > 0) {
      percentage = (remaining / (remaining + costs)) * 100;
    } else if (remaining > 0) {
      percentage = 50;
    }

    percentage = Math.max(0, Math.min(100, percentage));

    // Only yellow/amber if between 5% and 15%, otherwise green
    const isAtRisk = percentage >= 0 && percentage <= 15;

    return { percentage, isAtRisk, remaining };
  };

  const customRenderCellContent = (col, item) => {
    switch (col.key) {
      case "project_name":
        return (
          <button
            onClick={() => handleProjectTitleClick(item)}
            className={`hover:underline text-left font-semibold truncate max-w-[120px] sm:max-w-[200px] md:max-w-[280px]
              ${totalDaysLeft <= 0 ? "pointer-events-none text-gray-400" : "text-blue-600 hover:text-blue-800"}`}
            disabled={totalDaysLeft <= 0}
          >
            {item[col.key]}
          </button>
        );
      case "task":
        return (
          <button
            className="flex cursor-pointer items-center justify-center gap-1.5 text-blue-600 hover:text-blue-800 transition-colors mx-auto"
            onClick={() => handleTaskClick(item)}
            title={`${item.total_tasks ?? 0} Tasks - Click to view`}
          >
            <span className="text-xs font-semibold text-gray-700">
              {item.total_tasks ?? 0}
            </span>
            <BiTask className="text-xl sm:text-2xl text-blue-500" />
          </button>
        );
      case "daily_reporting_status":
      case "weekly_reporting_status":
      case "monthly_reporting_status": {
        const val = item[col.key];
        const { isAtRisk } = getBudgetStatus(item);

        return (
          <div className="flex items-center justify-center w-full">
            <button
              type="button"
              className="cursor-pointer transition-transform hover:scale-110 focus:outline-none"
              onClick={() => shareReporting(col.key, item.project_id)}
              title={
                typeof val === "string" && val
                  ? val
                  : !isAtRisk
                    ? "On Track"
                    : "At Risk"
              }
            >
              {!isAtRisk ? (
                <FaCheckCircle className="text-emerald-500 text-lg sm:text-xl" />
              ) : (
                <FaExclamationCircle className="text-amber-500 text-lg sm:text-xl" />
              )}
            </button>
          </div>
        );
      }
      case "agency_fees": {
        const feeValue = agencyFees[item.project_id] || item.agency_fees || 0;
        return isEditing[item.project_id] ? (
          <div className="flex items-center">
            <input
              type="number"
              value={tempBudgets[item.project_id] || ""}
              onChange={(e) =>
                setTempBudgets((prev) => ({
                  ...prev,
                  [item.project_id]: e.target.value,
                }))
              }
              className="w-20 border p-1"
            />
            <button
              onClick={() => handleAgencyFeeSave(item.project_id)}
              className="ml-1 rounded-md bg-green-500 p-1 text-white hover:bg-green-600"
            >
              ✅
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <span className="whitespace-nowrap">
              {formatCellValue(col.key, feeValue, col, item["currency"])}
            </span>
            <button
              onClick={() => toggleEditing(item.project_id, feeValue)}
              className="text-blue-500"
            >
              <FaPencilAlt />
            </button>
          </div>
        );
      }
      case "remaining_monthly_budget": {
        const { percentage, isAtRisk, remaining } = getBudgetStatus(item);

        const textColor = isAtRisk
          ? "text-amber-500 font-medium"
          : "text-emerald-600 font-medium";

        const barColor = isAtRisk ? "bg-amber-500" : "bg-emerald-500";

        const formattedVal = formatCellValue(
          col.key,
          remaining,
          col,
          item["currency"],
        );

        return (
          <div className="flex items-center justify-between gap-3 min-w-[190px] w-full">
            <div className="w-20 sm:w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden shrink-0">
              <div
                className={`h-1.5 rounded-full ${barColor}`}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className={`whitespace-nowrap ${textColor}`}>
              {formattedVal}
            </span>
          </div>
        );
      }
      default:
        return (
          <span className="whitespace-nowrap">
            {formatCellValue(col.key, item[col.key], col, item["currency"])}
          </span>
        );
    }
  };

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <h1 className="p-4 text-center text-xl text-[#4142dc]">
        Please select a project and an engine to view data.
      </h1>
    );
  }

  if (localData.length === 0) {
    return (
      <h1 className="p-4 text-center text-xl text-[#4142dc]">
        No data available for the selected filters.
      </h1>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-gray-200 shadow-sm overflow-x-auto bg-white w-full max-w-full">
        <RenderTable
          columns={columns}
          data={localData}
          variant="performance"
          renderCellContent={customRenderCellContent}
        />
      </div>

      {/* Status & Budget Legend */}
      <div className="flex flex-wrap items-center gap-6 mt-4 text-xs sm:text-sm text-gray-600 px-1">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 inline-block" />
          <span className="text-gray-700">On Track</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500 inline-block" />
          <span className="text-gray-700">At Risk (0% - 15% remaining)</span>
        </div>
      </div>

      <BudgetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveBudget}
        currency={currency}
        initialBudget={
          userBudget[editableRowIndex] ||
          (data[editableRowIndex] ? data[editableRowIndex].budget : "")
        }
        projectName={
          data[editableRowIndex] ? data[editableRowIndex].project_name : ""
        }
        platform="Google Ads"
      />
    </>
  );
}

function PaidAdsPage() {
  const [allApiData, setAllApiData] = useState([]);
  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isTableDownloading, setIsTableDownloading] = useState(false);
  const [showTableDownloadMenu, setShowTableDownloadMenu] = useState(false);

  const [isTableFullScreen, setIsTableFullScreen] = useState(false);
  const [currentProjects, setCurrentProjects] = useState([]);
  const [currentSearchEngine, setCurrentSearchEngine] = useState(
    ALL_SEARCH_ENGINES.map((e) => e.id),
  );

  const today = new Date();
  const firstDayOfYear = new Date(today.getFullYear(), 0, 1);

  const [dateRange, setDateRange] = useState([
    { startDate: firstDayOfYear, endDate: today, key: "selection" },
  ]);
  const [committedDateRange, setCommittedDateRange] = useState([
    { startDate: firstDayOfYear, endDate: today, key: "selection" },
  ]);

  const [agencyFees, setAgencyFees] = useState({});
  const [isDatePickerVisible, setDatePickerVisible] = useState(false);
  const [isColumnsMenuVisible, setColumnsMenuVisible] = useState(false);
  const [isTableVisible, setTableVisible] = useState(true);

  const [totalDaysLeft, setTotalDaysLeft] = useState(1);
  const [, setTotalProjects] = useState(1);

  const datePickerRef = useRef(null);
  const columnRef = useRef(null);
  const tableDownloadRef = useRef(null);

  const allProjectNames = useMemo(
    () => Array.from(new Set(allApiData.map((item) => item.project_name))),
    [allApiData],
  );

  const areALlSearchEngineSelected =
    ALL_SEARCH_ENGINES.length > 0 &&
    currentSearchEngine.length === ALL_SEARCH_ENGINES.length;
  const isSearchEngineSelectionIndeterminate =
    currentSearchEngine.length > 0 &&
    currentSearchEngine.length < ALL_SEARCH_ENGINES.length;

  const areAllProjectsSelected =
    allProjectNames.length > 0 &&
    currentProjects.length === allProjectNames.length;
  const isProjectSelectionIndeterminate =
    currentProjects.length > 0 &&
    currentProjects.length < allProjectNames.length;

  const dateRangeButtonLabel = useMemo(() => {
    const start = format(committedDateRange[0].startDate, "MMM dd, yyyy");
    const end = format(committedDateRange[0].endDate, "MMM dd, yyyy");
    return start === end ? start : `${start} - ${end}`;
  }, [committedDateRange]);

  const displayedData = useMemo(() => {
    const validPlatformIds = new Set(
      ALL_SEARCH_ENGINES.filter((engine) =>
        currentSearchEngine.includes(engine.id),
      ).flatMap((engine) => engine.platformIds),
    );
    return allApiData
      .filter((item) => validPlatformIds.has(item.platform))
      .filter((item) => currentProjects.includes(item.project_name));
  }, [allApiData, currentProjects, currentSearchEngine]);

  const toggleSearchEngine = (event) => {
    const { value } = event.target;
    if (value.includes("all-engine")) {
      setCurrentSearchEngine(
        areALlSearchEngineSelected ? [] : ALL_SEARCH_ENGINES.map((e) => e.id),
      );
    } else {
      setCurrentSearchEngine(
        typeof value === "string" ? value.split(",") : value,
      );
    }
  };

  const handleProjectChange = (event) => {
    const { value } = event.target;
    if (value.includes("all-projects")) {
      setCurrentProjects(areAllProjectsSelected ? [] : allProjectNames);
    } else {
      setCurrentProjects(typeof value === "string" ? value.split(",") : value);
    }
  };

  const handleApplyDateRange = () => {
    setCommittedDateRange(dateRange);
    setDatePickerVisible(false);
  };

  const handleToggleDatePicker = () => {
    if (!isDatePickerVisible) {
      setDateRange(committedDateRange);
    }
    setDatePickerVisible((prev) => !prev);
  };

  const handleTableDownload = async (type) => {
    setShowTableDownloadMenu(false);
    setIsTableDownloading(true);
    try {
      const filename = `Paid_Ads_Report_${format(new Date(), "yyyy-MM-dd")}.${type}`;
      const dateRangeStr =
        committedDateRange[0].startDate === committedDateRange[0].endDate
          ? "All time"
          : `${format(committedDateRange[0].startDate, "MMM dd, yyyy")} - ${format(
              committedDateRange[0].endDate,
              "MMM dd, yyyy",
            )}`;

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(
          displayedData,
          columns,
          dateRangeStr,
          filename,
          "Paid Ads Report",
        );
      } else if (type === "csv") {
        await generateCSV(
          displayedData,
          columns,
          dateRangeStr,
          filename,
          "Paid Ads Report",
        );
      }
    } catch (error) {
      console.error(`Failed to generate Table ${type.toUpperCase()}:`, error);
    } finally {
      setIsTableDownloading(false);
    }
  };

  const fetchAccountDetails = async () => {
    try {
      const res = await axios.post(API_URLS.PROJECT_SUMMARY, {
        email: localStorage.getItem("email"),
      });
      if (res.data) {
        setTotalDaysLeft(res.data.remainingDays);
        setTotalProjects(res.data.totalProjects);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchAdsData = async () => {
      setIsLoading(true);
      await fetchAccountDetails();
      const email = localStorage.getItem("email");

      // Query projects to fetch task counts accurately
      let projectTaskMap = {};
      try {
        const projRes = await axios.post(
          "https://api.confidanto.com/projects-data/project-list",
          { email },
        );
        if (Array.isArray(projRes.data)) {
          projRes.data.forEach((p) => {
            const pid = p.project_id || p.id;
            if (pid) projectTaskMap[pid] = p.total_tasks ?? 0;
            if (p.name) projectTaskMap[p.name] = p.total_tasks ?? 0;
          });
        }
      } catch (err) {
        console.error("Error fetching project list for tasks:", err);
      }

      if (DEMO_EMAILS.includes(email)) {
        const demoData = RevisedProjectData.map((item) => ({
          ...item,
          total_tasks:
            item.total_tasks ??
            projectTaskMap[item.project_id] ??
            projectTaskMap[item.project_name] ??
            0,
        }));
        setAllApiData(demoData);
        setCurrentProjects(
          Array.from(new Set(demoData.map((item) => item.project_name))),
        );
        setIsLoading(false);
        return;
      }

      const payload = {
        email,
        start_date: format(committedDateRange[0].startDate, "yyyy-MM-dd"),
        end_date: format(committedDateRange[0].endDate, "yyyy-MM-dd"),
      };

      try {
        const res = await axios.post(API_URLS.ADS_OVERVIEW_DATA, payload);
        if (Array.isArray(res.data) && res.data.length > 0) {
          const formattedData = res.data.map((item) => ({
            ...item,
            ...item.metrics,
          }));
          const mergedData = formattedData.map((apiItem) => {
            const localItem =
              RevisedProjectData.find(
                (p) => p.project_name === apiItem.project_name,
              ) || {};
            const totalTasks =
              apiItem.total_tasks ??
              projectTaskMap[apiItem.project_id] ??
              projectTaskMap[apiItem.project_name] ??
              localItem.total_tasks ??
              0;
            return {
              ...localItem,
              ...apiItem,
              total_tasks: totalTasks,
            };
          });
          setAllApiData(mergedData);
          setCurrentProjects(
            Array.from(new Set(mergedData.map((item) => item.project_name))),
          );
        } else {
          setAllApiData([]);
          setCurrentProjects([]);
        }
      } catch (error) {
        setAllApiData([]);
        setCurrentProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    localStorage.removeItem("project_name");
    window.dispatchEvent(
      new CustomEvent("localStorageChange", {
        detail: { key: "project_name" },
      }),
    );
    fetchAdsData();
  }, [committedDateRange]);

  useEffect(() => {
    const initializeGlobalData = async () => {
      const email = localStorage.getItem("email");
      try {
        const res = await axios.post(API_URLS.HEADER_DATA, { email });
        setUserData(res.data.userData);
      } catch (error) {
        setUserData(null);
      }

      try {
        const res = await axios.post(API_URLS.GET_CUSTOM_COLUMNS, { email });
        if (res?.data?.data) {
          const customColumns = res.data.data.map((item) => ({
            key: item.custom_column,
            title: item.custom_column,
            visible: false,
            isLocked: false,
            id: item.id,
            category: "Custom Columns",
          }));
          setColumns((prev) => [...prev, ...customColumns]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    initializeGlobalData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setDatePickerVisible(false);
      }
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setColumnsMenuVisible(false);
      }
      if (
        tableDownloadRef.current &&
        !tableDownloadRef.current.contains(event.target)
      ) {
        setShowTableDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  };

  const isSelectionError =
    allApiData.length > 0 &&
    (currentProjects.length === 0 || currentSearchEngine.length === 0);

  return (
    <div className="px-2 md:px-6 lg:px-12 py-6 overflow-x-hidden w-full max-w-full min-w-0">
      <div className="rounded-lg bg-white shadow p-4 sm:p-6 md:p-8 w-full max-w-full min-w-0">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          {userData ? (
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold uppercase text-[#070a74]">
              {`Team ${userData.username}`}
            </h1>
          ) : (
            <div className="h-8 w-44 bg-gray-200 rounded animate-pulse" />
          )}
          <div className="m-4 mr-8 flex flex-wrap items-center gap-3">
            <div className="relative z-30" ref={datePickerRef}>
              <button
                onClick={handleToggleDatePicker}
                className="w-60 border border-gray-400 p-2 text-base rounded hover:bg-gray-50"
              >
                {dateRangeButtonLabel}
              </button>
              {isDatePickerVisible && (
                <div className="absolute right-2 z-30 mt-2 bg-white shadow-lg border rounded">
                  <DateRangePicker
                    onChange={(item) => setDateRange([item.selection])}
                    ranges={dateRange}
                    months={1}
                    direction="horizontal"
                    maxDate={getYesterday()}
                  />
                  <div className="mx-2 mb-2 flex justify-end">
                    <Button
                      onClick={handleApplyDateRange}
                      variant="contained"
                      size="small"
                      className="mt-2"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="bg-white pb-6 pt-4 px-2 sm:px-6 md:px-12 w-full max-w-full min-w-0 box-border">
          <div className="flex flex-col mb-5 mt-2 w-full max-w-full min-w-0">
            <div className="w-full max-w-full min-w-0 bg-white">
              <PaidAdsChart
                previousDates={{
                  curr_start_date: format(
                    committedDateRange[0].startDate,
                    "yyyy-MM-dd",
                  ),
                  curr_end_date: format(
                    committedDateRange[0].endDate,
                    "yyyy-MM-dd",
                  ),
                }}
              />
            </div>
          </div>

          <div
            className={`flex flex-col w-full max-w-full min-w-0 transition-all duration-300 ease-in-out ${
              isTableFullScreen
                ? "fixed inset-0 z-50 bg-white p-4 sm:p-6 md:p-8 overflow-y-auto"
                : ""
            }`}
          >
            <div className="mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <div className="flex flex-col sm:flex-row w-full md:w-3/4 gap-4">
                <FormControl className="w-full sm:w-1/2 md:w-1/3">
                  <InputLabel>Projects</InputLabel>
                  <Select
                    multiple
                    value={currentProjects}
                    onChange={handleProjectChange}
                    input={<OutlinedInput label="Projects" />}
                    renderValue={(selected) =>
                      selected.length === allProjectNames.length
                        ? "All Projects"
                        : selected.join(", ")
                    }
                    disabled={allProjectNames.length === 0}
                  >
                    <MenuItem value="all-projects">
                      <Checkbox
                        checked={areAllProjectsSelected}
                        indeterminate={isProjectSelectionIndeterminate}
                      />
                      <ListItemText primary="All Projects" />
                    </MenuItem>
                    {allProjectNames.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox
                          checked={currentProjects.indexOf(name) > -1}
                        />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl className="w-full sm:w-1/2 md:w-1/3">
                  <InputLabel>Engine</InputLabel>
                  <Select
                    multiple
                    value={currentSearchEngine}
                    onChange={toggleSearchEngine}
                    input={<OutlinedInput label="Engine" />}
                    renderValue={(selected) => {
                      if (selected.length === ALL_SEARCH_ENGINES.length)
                        return "All Engine";
                      return ALL_SEARCH_ENGINES.filter((e) =>
                        selected.includes(e.id),
                      )
                        .map((e) => e.name)
                        .join(", ");
                    }}
                    disabled={ALL_SEARCH_ENGINES.length === 0}
                  >
                    <MenuItem value="all-engine">
                      <Checkbox
                        checked={areALlSearchEngineSelected}
                        indeterminate={isSearchEngineSelectionIndeterminate}
                      />
                      <ListItemText primary="All Engine" />
                    </MenuItem>
                    {ALL_SEARCH_ENGINES.map((engine) => (
                      <MenuItem key={engine.id} value={engine.id}>
                        <Checkbox
                          checked={currentSearchEngine.indexOf(engine.id) > -1}
                        />
                        <ListItemText primary={engine.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Action Buttons Section */}
              <div className="flex items-center space-x-2">
                {/* Download Button */}
                <div
                  className={`relative transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
                  ref={tableDownloadRef}
                >
                  <button
                    disabled={isLoading || isTableDownloading}
                    className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                      isLoading || isTableDownloading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-slate-100"
                    } ${showTableDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                    onClick={() => setShowTableDownloadMenu((prev) => !prev)}
                  >
                    {isTableDownloading ? (
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <MdOutlineFileDownload className="text-md" />
                    )}
                    <span className="text-sm">
                      {isTableDownloading ? "Downloading" : "Download"}
                    </span>
                  </button>
                  {showTableDownloadMenu && (
                    <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                      <div
                        className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                        onClick={() => handleTableDownload("pdf")}
                      >
                        .PDF
                      </div>
                      <div
                        className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                        onClick={() => handleTableDownload("csv")}
                      >
                        .CSV
                      </div>
                    </div>
                  )}
                </div>

                {/* Expand / Collapse Button */}
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isTableFullScreen
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => setIsTableFullScreen(!isTableFullScreen)}
                >
                  {isTableFullScreen ? (
                    <FaCompress className="text-md" />
                  ) : (
                    <FaExpand className="text-md" />
                  )}
                  <span className="text-sm">
                    {isTableFullScreen ? "Collapse" : "Expand"}
                  </span>
                </button>

                {/* Columns Menu Button */}
                <div className="relative" ref={columnRef}>
                  <button
                    disabled={isLoading}
                    className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                      isColumnsMenuVisible
                        ? "bg-blue-100 text-blue-600"
                        : "bg-transparent text-gray-600"
                    } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                    onClick={() => setColumnsMenuVisible((prev) => !prev)}
                  >
                    <FaColumns className="text-md" />
                    <span className="text-sm">Columns</span>
                  </button>
                  {isColumnsMenuVisible && (
                    <ModifyColumns
                      columns={columns}
                      setColumns={setColumns}
                      setTableVisible={setTableVisible}
                      setShowColumnsMenu={setColumnsMenuVisible}
                      isPerformancePage={true}
                    />
                  )}
                </div>
              </div>
            </div>

            {isTableVisible && (
              <main
                className={`w-full max-w-full min-w-0 ${!isTableFullScreen ? "mb-32 mt-6" : "mt-6 flex-1"}`}
              >
                <GoogleAdsTable
                  data={displayedData}
                  columns={columns}
                  currency={CURRENCY_SYMBOL}
                  agencyFees={agencyFees}
                  setAgencyFees={setAgencyFees}
                  isLoading={isLoading}
                  error={isSelectionError}
                  onSaveBudget={(newBudget) => {}}
                  totalDaysLeft={totalDaysLeft}
                />
              </main>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaidAdsPage;
